import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const skills = [
  { name: 'HTML', level: 95, color: 'var(--rose)' },
  { name: 'CSS', level: 90, color: 'var(--aqua)' },
  { name: 'JavaScript', level: 72, color: 'var(--sun)' },
  { name: 'React', level: 68, color: 'var(--lime)' },
  { name: 'Flutter', level: 62, color: 'var(--aqua)' },
  { name: '.NET', level: 58, color: 'var(--violet)' },
  { name: 'PHP', level: 62, color: 'var(--violet)' },
  { name: 'WordPress', level: 85, color: 'var(--aqua)' },
];

const education = [
  {
    title: 'Bachelor of Computer Application',
    place: 'Government Arts and Science College, Nemmeli',
    period: '2021 - 2024',
    desc: 'Built a strong base in programming, web development, software concepts, and practical project work.',
  },
];

const experience = [
  {
    title: 'Freelance Web Developer',
    place: 'Self Employed',
    period: '2024 - Present',
    desc: 'Designed and developed responsive portfolios, product websites, business pages, and PHP-based web projects.',
  },
];

function SkillBar({ name, level, color, inView, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.07 }}
      style={{ marginBottom: 22 }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 8 }}>
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 13, letterSpacing: 1, textTransform: 'uppercase' }}>{name}</span>
        <span style={{ color, fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 13 }}>{level}%</span>
      </div>
      <div style={{ height: 9, background: 'rgba(255,255,255,0.07)', borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
        <motion.div
          initial={{ width: 0 }}
          animate={inView ? { width: `${level}%` } : { width: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.25 + index * 0.05 }}
          style={{ height: '100%', background: `linear-gradient(90deg, ${color}, var(--sun))`, borderRadius: 8 }}
        />
      </div>
    </motion.div>
  );
}

function TimelineItem({ item, accent }) {
  return (
    <div style={{ position: 'relative', paddingLeft: 30, paddingBottom: 28 }}>
      <span
        style={{
          position: 'absolute',
          left: 0,
          top: 4,
          width: 13,
          height: 13,
          borderRadius: 8,
          background: accent,
          boxShadow: `0 0 0 5px color-mix(in srgb, ${accent} 20%, transparent)`,
        }}
      />
      <span style={{ position: 'absolute', left: 6, top: 22, bottom: 0, width: 1, background: 'rgba(255,255,255,0.12)' }} />
      <div style={{ color: accent, fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 800, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 6 }}>
        {item.period}
      </div>
      <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 18, lineHeight: 1.25, marginBottom: 5 }}>{item.title}</h4>
      <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 7 }}>{item.place}</p>
      <p style={{ color: 'var(--soft)', fontSize: 14 }}>{item.desc}</p>
    </div>
  );
}

export default function Skills() {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <section id="skill" ref={ref} className="section-pad">
      <div className="ghost-text">SKILLS</div>

      <div className="section-shell">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          style={{ textAlign: 'center', marginBottom: 58 }}
        >
          <div className="eyebrow" style={{ justifyContent: 'center' }}>What I use</div>
          <h2 className="section-title">Skills that make the pages feel alive.</h2>
          <p className="section-copy" style={{ marginLeft: 'auto', marginRight: 'auto' }}>
            A practical mix of React, Flutter, .NET, CMS, frontend, and backend basics for building client-ready websites and app interfaces.
          </p>
        </motion.div>

        <div className="skills-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28, alignItems: 'start' }}>
          <motion.div
            initial={{ opacity: 0, x: -34 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.12 }}
            className="premium-card"
            style={{ padding: 30 }}
          >
            <div className="color-line" style={{ marginBottom: 26 }} />
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 24, marginBottom: 28 }}>Technical Stack for Web and App Development</h3>
            {skills.map((skill, index) => <SkillBar key={skill.name} {...skill} inView={inView} index={index} />)}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 34 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.18 }}
            className="premium-card"
            style={{ padding: 30 }}
          >
            <div className="color-line" style={{ marginBottom: 26 }} />
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 24, marginBottom: 28 }}>Learning and Experience</h3>
            {education.map(item => <TimelineItem key={item.title} item={item} accent="var(--aqua)" />)}
            {experience.map(item => <TimelineItem key={item.title} item={item} accent="var(--rose)" />)}
          </motion.div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .skills-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
